import React, { useState } from 'react';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import ko from '../../language/Ko-kr.json';
import en from '../../language/En-eng.json';
import { ResponsiveShell } from '../../components/ResponsiveShell';

const isKorean = true; // TODO: 실제 언어 설정 연동
const t = isKorean ? ko : en;

/* ─── Global ─────────────────────────────────────────────────────────────── */
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Jua&family=Noto+Sans+KR:wght@400;500;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { height: 100%; overflow: hidden; }
  body {
    font-family: 'Noto Sans KR', sans-serif;
    background: #E8E0C8;
    color: #5C5040;
    -webkit-font-smoothing: antialiased;
    height: 100dvh;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  #root {
    width: 100%;
    height: 100dvh;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
  }
`;

/* ─── Animations ─────────────────────────────────────────────────────────── */
const slideUp = keyframes`
  from { transform: translateY(100%); opacity: 0.5; }
  to { transform: translateY(0); opacity: 1; }
`;

/* ─── Phone Shell ────────────────────────────────────────────────────────── */

/* ─── Nav Bar ────────────────────────────────────────────────────────────── */
const PhoneNavBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 20px 12px;
  flex-shrink: 0;
  background: rgba(249, 246, 232, 0.95);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1.5px solid #EDE9D5;
  z-index: 10;
`;

const BackBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #EDE9D5;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s, transform 0.15s;
  color: #5C5040;
  &:hover { background: #E0D8C0; transform: scale(1.05); }
  &:active { transform: scale(0.95); }
`;

const NavTitle = styled.h2`
  font-family: 'Jua', sans-serif;
  font-size: 18px;
  color: #3D3020;
  flex: 1;
  text-align: center;
  margin-right: 36px; /* offset for back button to center title */
`;

/* ─── Body ───────────────────────────────────────────────────────────────── */
const PhoneBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 32px 24px;
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InputLabel = styled.label`
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #5C5040;
`;

const StyledInput = styled.input`
  width: 100%;
  border: 1.5px solid #D8D0B8;
  border-radius: 16px;
  background: #FFF;
  padding: 14px 18px;
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 15px;
  color: #3D3020;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  
  &:focus {
    border-color: #E07040;
    box-shadow: 0 0 0 3px rgba(224, 112, 64, 0.1);
  }
  
  &::placeholder {
    color: #C8C0A8;
  }
`;

const PrimaryBtn = styled.button`
  background: #E07040; /* Orange color for signup */
  color: #fff;
  border: none;
  border-radius: 16px;
  padding: 16px;
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(224, 112, 64, 0.3);
  transition: transform 0.15s, background 0.2s;
  margin-top: 24px;
  
  &:hover { background: #D06030; transform: translateY(-2px); }
  &:active { transform: translateY(1px); }
  
  &:disabled {
    background: #C8C0A8;
    box-shadow: none;
    cursor: not-allowed;
    transform: none;
  }
`;

const Description = styled.p`
  font-size: 14px;
  color: #8C8070;
  text-align: center;
  margin-bottom: 24px;
  line-height: 1.5;
`;

/* ─── Component ──────────────────────────────────────────────────────────── */
const SignupLogic: React.FC = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [idTag, setIdTag] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [betaKey, setBetaKey] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [nameError, setNameError] = useState('');
  const [idTagError, setIdTagError] = useState('');
  const [betaKeyError, setBetaKeyError] = useState('');
  const [popup, setPopup] = useState<{ title: string; msg: string; onClose?: () => void } | null>(null);
  const showPopup = (title: string, msg: string, onClose?: () => void) => setPopup({ title, msg, onClose });

  const isFormValid = email && name && idTag.length > 1 && password && password === passwordConfirm && betaKey && !passwordError && !emailError && !nameError && !idTagError;

  const handleDuplicateCheck = async (type: 'email' | 'username' | 'id_tag', value: string, setError: (msg: string) => void, errorMsg: string) => {
    if (!value) {
      setError('');
      return;
    }
    try {
      // NOTE: 실제 백엔드 중복 확인 API 주소에 맞게 수정이 필요할 수 있습니다.
      const endpoint = type === 'id_tag' ? 'idtag' : type;
      const url = `/user/check-${endpoint}?${type}=${encodeURIComponent(value)}`;
      const response = await fetch(url).catch(() => null);

      if (!response) return;

      if (response.status === 409) {
        setError(errorMsg);
      } else if (response.ok) {
        const data = await response.json().catch(() => null);
        if (data && data.isDuplicate) {
          setError(errorMsg);
        } else {
          setError('');
        }
      } else {
        // 백엔드 API가 아직 없어서 404 에러 등이 날 경우, 중복으로 처리하여 가입을 막지 않도록 통과시킵니다.
        console.warn('Duplicate check API not found or error. Bypassing check.');
        setError('');
      }
    } catch (e) {
      console.error('Duplicate check error:', e);
    }
  };

  const handleEmailBlur = () => handleDuplicateCheck('email', email, setEmailError, t.signup.duplicateEmail);
  const handleNameBlur = () => handleDuplicateCheck('username', name, setNameError, t.signup.duplicateName);
  const handleIdTagBlur = () => handleDuplicateCheck('id_tag', idTag, setIdTagError, t.signup.duplicateIdTag);

  const handleIdTagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    if (val === '') {
      setIdTag('');
      return;
    }
    if (!val.startsWith('@')) {
      val = '@' + val.replace(/@/g, '');
    }
    // 영어, 숫자, _, - 만 허용
    val = '@' + val.substring(1).replace(/[^a-zA-Z0-9_-]/g, '');
    setIdTag(val);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // 한글 및 비표준 문자 입력 방지
    const val = e.target.value.replace(/[^\x00-\x7F]/g, '');
    if (/['";#]/.test(val) || val.includes('--')) {
      setPasswordError(t.signup.passwordInvalid);
    } else {
      setPasswordError('');
    }
    setPassword(val);
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/user/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_email: email, password: password, user_name: name, user_alias: idTag, beta_key: betaKey }),
      });

      if (response.ok) {
        localStorage.setItem('has_beta_key', 'true');
        showPopup('회원가입 완료', t.signup.alertSuccess, () => { window.location.href = '/login'; });
      } else {
        const errorData = await response.json().catch(() => null);
        showPopup('회원가입 안내', `${t.signup.alertFail}${errorData?.message || t.signup.alertFailDefault}`);
      }
    } catch (error) {
      console.error('Signup error:', error);
      showPopup('회원가입 안내', t.signup.alertError);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <GlobalStyle />
      <ResponsiveShell>
        <PhoneNavBar>
          <BackBtn onClick={() => { window.location.href = '/login'; }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </BackBtn>
          <NavTitle>{t.signup.title}</NavTitle>
        </PhoneNavBar>

        <PhoneBody>
          <Description>
            {t.signup.desc.split('\n').map((line: string, i: number, arr: string[]) => (
              <React.Fragment key={i}>{line}{i < arr.length - 1 && <br />}</React.Fragment>
            ))}
          </Description>

          <FormContainer onSubmit={handleSignup}>
            <InputGroup>
              <InputLabel>{t.signup.emailLabel}</InputLabel>
              <StyledInput
                type="email"
                placeholder={t.signup.emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={handleEmailBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleEmailBlur();
                  }
                }}
                required
              />
              {emailError && (
                <p style={{ color: '#EA4335', fontSize: '13px', marginTop: '-4px', fontWeight: 500 }}>
                  {emailError}
                </p>
              )}
            </InputGroup>

            <InputGroup>
              <InputLabel>{t.signup.nameLabel}</InputLabel>
              <StyledInput
                type="text"
                placeholder={t.signup.namePlaceholder}
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={handleNameBlur}
                required
              />
              {nameError && (
                <p style={{ color: '#EA4335', fontSize: '13px', marginTop: '-4px', fontWeight: 500 }}>
                  {nameError}
                </p>
              )}
            </InputGroup>

            <InputGroup>
              <InputLabel>{t.signup.idTagLabel}</InputLabel>
              <StyledInput
                type="text"
                placeholder={t.signup.idTagPlaceholder}
                value={idTag}
                onChange={handleIdTagChange}
                onBlur={handleIdTagBlur}
                required
              />
              {idTagError && (
                <p style={{ color: '#EA4335', fontSize: '13px', marginTop: '-4px', fontWeight: 500 }}>
                  {idTagError}
                </p>
              )}
            </InputGroup>

            <InputGroup>
              <InputLabel>{t.signup.passwordLabel}</InputLabel>
              <StyledInput
                type="password"
                placeholder={t.signup.passwordPlaceholder}
                value={password}
                onChange={handlePasswordChange}
                required
              />
            </InputGroup>

            {passwordError && (
              <p style={{ color: '#EA4335', fontSize: '13px', marginTop: '-4px', fontWeight: 500 }}>
                {passwordError}
              </p>
            )}

            <InputGroup>
              <InputLabel>{t.signup.passwordConfirmLabel}</InputLabel>
              <StyledInput
                type="password"
                placeholder={t.signup.passwordConfirmPlaceholder}
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value.replace(/[^\x00-\x7F]/g, ''))}
                required
              />
            </InputGroup>

            {password && passwordConfirm && password !== passwordConfirm && (
              <p style={{ color: '#EA4335', fontSize: '13px', marginTop: '-4px', fontWeight: 500 }}>
                {t.signup.passwordMismatch}
              </p>
            )}

            <InputGroup>
              <InputLabel>{t.signup.betaKeyLabel}</InputLabel>
              <StyledInput
                type="text"
                placeholder={t.signup.betaKeyPlaceholder}
                value={betaKey}
                onChange={(e) => {
                  setBetaKey(e.target.value);
                  setBetaKeyError('');
                }}
                required
              />
              {betaKeyError && (
                <p style={{ color: '#EA4335', fontSize: '13px', marginTop: '-4px', fontWeight: 500 }}>
                  {betaKeyError}
                </p>
              )}
            </InputGroup>

            <PrimaryBtn type="submit" disabled={!isFormValid || isLoading}>
              {isLoading ? t.signup.btnLoading : t.signup.btnSubmit}
            </PrimaryBtn>
          </FormContainer>
        </PhoneBody>

        {/* ── 인라인 팝업 ── */}
        {popup && (
          <div style={{
            position: 'fixed', inset: 0,
            background: 'rgba(40,30,20,0.55)',
            backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
            zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px', boxSizing: 'border-box'
          }}>
            <div style={{
              background: '#FDFBF4', borderRadius: '20px', padding: '28px 24px 20px',
              width: '90%', maxWidth: '340px', boxShadow: '0 16px 40px rgba(40,30,20,0.3)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center'
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: '50%', background: '#FFF0EA',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 14, color: '#E07040'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
              </div>
              <p style={{ fontWeight: 700, fontSize: 17, color: '#3D3020', marginBottom: 8 }}>{popup.title}</p>
              <p style={{ fontSize: 14, color: '#6C5D47', lineHeight: 1.6, marginBottom: 22, whiteSpace: 'pre-line' }}>{popup.msg}</p>
              <button
                onClick={() => { const cb = popup.onClose; setPopup(null); cb?.(); }}
                style={{
                  width: '100%', padding: '13px', borderRadius: 12, border: 'none', cursor: 'pointer',
                  background: 'linear-gradient(135deg,#E07040,#D05828)', color: '#fff',
                  fontSize: 15, fontWeight: 700, boxShadow: '0 4px 14px rgba(224,112,64,0.3)'
                }}
              >확인</button>
            </div>
          </div>
        )}
      </ResponsiveShell>
    </>
  );
};

export default SignupLogic;
